import { getApiBaseUrl } from './apiUrl'
import api from './api'
import axios from 'axios'

export interface User {
  id: string
  email: string
  full_name: string
  first_name?: string
  last_name?: string
  job_title?: string
  phone?: string
  department?: string
  country?: string
  role: string
  organization_id: string
  organization_name?: string
  is_active: boolean
  is_super_admin: boolean
  can_share_internally?: boolean
  can_create_external_share_links?: boolean
  can_download_gif?: boolean
  can_download_mp4?: boolean
  can_use_sales_enablement?: boolean
  sales_enablement_enabled?: boolean
  organization_sales_enablement_enabled?: boolean
  avatar_url?: string
  created_at: string
}

export interface AuthResponse {
  user: User
  access_token: string
  refresh_token: string
  token_type: string
  must_change_password?: boolean
}

export interface RegisterData {
  email: string
  password: string
  full_name: string
  first_name?: string
  last_name?: string
  job_title?: string
  phone?: string
  department?: string
  country?: string
  organization_name?: string
}

export interface LoginData {
  email: string
  password: string
}

export interface ForgotPasswordResponse {
  message: string
  success?: boolean
  development_mode?: boolean
  email_sent?: boolean
  reset_token?: string
  reset_url?: string
}

export const authService = {
  // Register new user
  async register(data: RegisterData): Promise<AuthResponse> {
    // Use axios directly to avoid interceptors interfering with registration
    const API_URL = getApiBaseUrl()
    const url = `${API_URL}/api/v1/auth/register`

    try {
      const response = await axios.post(url, data, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      return response.data
    } catch (error: any) {
      console.error('Register error:', error)
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
      })
      throw error
    }
  },

  // Login user
  async login(data: LoginData): Promise<AuthResponse> {
    // OAuth2PasswordRequestForm expects form data with 'username' and 'password'
    const formData = new URLSearchParams()
    formData.append('username', data.email)  // OAuth2 uses 'username' for email
    formData.append('password', data.password)
    
    // Use axios directly to avoid interceptors interfering with login
    const API_URL = getApiBaseUrl()
    const url = `${API_URL}/api/v1/auth/login`

    try {
      const response = await axios.post(
        url,
        formData.toString(),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      )
      console.log('Login response:', response.data)
      return response.data
    } catch (error: any) {
      console.error('Login error:', error)
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        request: error.request,
      })
      throw error
    }
  },

  // Logout user
  async logout(): Promise<void> {
    await api.post('/auth/logout')
  },

  // Refresh access token
  async refreshToken(refreshToken: string): Promise<{ access_token: string; refresh_token?: string; token_type: string }> {
    const response = await api.post('/auth/refresh', { refresh_token: refreshToken })
    return response.data
  },

  // Get current user (verify token)
  async getCurrentUser(): Promise<User> {
    const response = await api.get('/auth/me')
    return response.data
  },

  // Request password reset
  async forgotPassword(email: string): Promise<ForgotPasswordResponse> {
    // Use axios directly to avoid interceptors
    const API_URL = getApiBaseUrl()
    const url = `${API_URL}/api/v1/auth/forgot-password`

    try {
      const response = await axios.post(
        url,
        { email },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )
      return response.data
    } catch (error: any) {
      console.error('Forgot password error:', error)
      throw error
    }
  },

  // Reset password with token
  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    // Use axios directly to avoid interceptors
    const API_URL = getApiBaseUrl()
    const url = `${API_URL}/api/v1/auth/reset-password`

    try {
      const response = await axios.post(
        url,
        { token, new_password: newPassword },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )
      return response.data
    } catch (error: any) {
      console.error('Reset password error:', error)
      throw error
    }
  },
}

// Export authAPI alias for backward compatibility
export const authAPI = authService
